/**
 * FileIcon
 *
 * Thin wrapper around the unified <Icon> component for cases where callers
 * have a filename (and optionally an entityType) but no full entity object.
 *
 * All icon resolution is delegated to iconService via fileTypeIcons.ts, so
 * every surface in the app shows the same icon for the same file format.
 */
import React from 'react';
import { Icon } from '../Icon';

interface FileIconProps {
  filename: string;
  entityType?: string;
  size?: number;
  className?: string;
}

const FileIcon: React.FC<FileIconProps> = ({
  filename,
  entityType,
  size = 24,
  className = '',
}) => {
  // The synthetic _id must be unique per filename — iconService's cache key is
  // built from (entityType, _id, icon), so a constant _id here would collide
  // across every distinct file rendered through this generic path and serve
  // back whichever file's icon happened to be resolved (and cached) first.
  const entityForIcon = {
    _id: `file-icon:${entityType || 'File'}:${filename}`,
    entityType: entityType || 'File',
    name: filename,
  } as any;

  const iconSize = size <= 16 ? 'small' : size <= 20 ? 'small' : 'medium';

  return (
    <Icon
      entity={entityForIcon}
      filename={filename}
      size={iconSize as 'small' | 'medium' | 'large'}
      className={className}
    />
  );
};

export default FileIcon;
